// Inject a search input after .hulkapps_option_name within each .hulkapps_option.cb_render
// once Hulk has rendered its options into the DOM.
function injectOptionSearchInputs() {
  document
    .querySelectorAll(".hulkapps_option.cb_render")
    .forEach((optionBlock) => {
      if (optionBlock.querySelector(".mto-search")) return; // already injected

      const optionName = optionBlock.querySelector(".hulkapps_option_name");
      if (!optionName) return;

      // Accordion toggle — keyboard-accessible. Hulkapps renders the name as
      // a non-interactive element, so we add tabindex/role/aria-expanded and
      // handle Enter and Space ourselves. Once open, the search input and
      // checkbox options are real form controls so the natural tab order
      // takes over (checkboxes are made focusable via CSS — see
      // visually-hidden replacement of the previous display: none rule).
      optionName.setAttribute("tabindex", "0");
      optionName.setAttribute("role", "button");
      optionName.setAttribute("aria-expanded", "false");
      const toggleAccordion = () => {
        const isOpen = optionBlock.classList.toggle("cb_render--open");
        optionName.setAttribute("aria-expanded", isOpen ? "true" : "false");
      };
      optionName.addEventListener("click", toggleAccordion);
      optionName.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          toggleAccordion();
        }
      });
      const searchInput = document.createElement("input");
      searchInput.type = "search";
      searchInput.className = "mto-search";
      searchInput.placeholder = "Search ingredients";

      // The .hulkapps_check_option label is the focusable element for each
      // ingredient — its inner checkbox is visually hidden and stays out of
      // the tab order (tabindex=-1). Visible labels are tabbable (0); items
      // hidden by the active search query are removed from tab order (-1).
      // Browsers don't translate keydown on a focused label into a click,
      // so we forward Enter/Space ourselves; the click cascades through
      // Hulkapps' own label handler to toggle the underlying checkbox.
      const setOptionTabindex = (option, isHidden) => {
        const input = option.querySelector('input[type="checkbox"]');
        option.setAttribute("tabindex", isHidden ? "-1" : "0");
        if (input) input.setAttribute("tabindex", "-1");
      };
      optionBlock
        .querySelectorAll(".hulkapps_option_value .hulkapps_check_option")
        .forEach((option) => setOptionTabindex(option, false));
      searchInput.addEventListener("input", () => {
        const query = searchInput.value.trim().toLowerCase();
        optionBlock
          .querySelectorAll(".hulkapps_option_value .hulkapps_check_option")
          .forEach((option) => {
            const label = (
              option.getAttribute("aria-label") || ""
            ).toLowerCase();
            const isHidden = query !== "" && !label.includes(query);
            option.hidden = isHidden;
            setOptionTabindex(option, isHidden);
          });
      });
      const optionValue = optionBlock.querySelector(".hulkapps_option_value");
      const wrapper = document.createElement("div");
      wrapper.className = "mto-options-wrapper";
      wrapper.appendChild(searchInput);
      if (optionValue) wrapper.appendChild(optionValue);
      optionName.insertAdjacentElement("afterend", wrapper);

      // Only create the selected summary for blocks that contain checkboxes (ingredients)
      const checkboxes = optionBlock.querySelectorAll(
        '.hulkapps_check_option input[type="checkbox"]'
      );
      if (checkboxes.length > 0) {
        createIngredientsSummary(optionBlock, checkboxes);
      }
    });
}
function createIngredientsSummary(cbRender, checkboxes) {
  const summary = document.createElement("div");
  summary.className = "mto-selected-summary";
  const counter = document.createElement("p");
  counter.className = "mto-selected-counter";
  const pillsContainer = document.createElement("div");
  pillsContainer.className = "mto-selected-pills";
  summary.appendChild(counter);
  summary.appendChild(pillsContainer);
  cbRender.insertAdjacentElement("afterend", summary);

  // data-max on the cb_render parent caps how many ingredients can be picked
  // and feeds the visible "N/MAX" counter. If absent or non-numeric, fall
  // back to the total option count and don't enforce a cap.
  const parsedMax = parseInt(cbRender.dataset.max ?? "", 10);
  const hasMax = Number.isFinite(parsedMax) && parsedMax > 0;
  const denominator = hasMax ? parsedMax : checkboxes.length;

  // Block unchecked label clicks (Hulkapps toggles via label, not the native
  // checkbox) once we've hit the cap. Capture phase so we intercept before
  // Hulk's own listener. Already-checked options must remain clickable so
  // users can deselect.
  if (hasMax) {
    checkboxes.forEach((cb) => {
      const label = cb.closest(".hulkapps_check_option");
      if (!label) return;
      label.addEventListener(
        "click",
        (e) => {
          if (cb.checked) return;
          const checkedCount = Array.from(checkboxes).filter(
            (c) => c.checked
          ).length;
          if (checkedCount >= parsedMax) {
            e.preventDefault();
            e.stopPropagation();
          }
        },
        true
      );
    });
  }
  function updateSummary() {
    const checkedBoxes = Array.from(checkboxes).filter((cb) => cb.checked);
    counter.innerHTML = `Ingredients Selected <span class="mto-selected-counter__fraction">${checkedBoxes.length}/${denominator}</span>`;
    if (hasMax) {
      const atMax = checkedBoxes.length >= parsedMax;
      checkboxes.forEach((cb) => {
        const label = cb.closest(".hulkapps_check_option");
        if (!label) return;
        const shouldDisable = atMax && !cb.checked;
        label.classList.toggle("mto-check-option--disabled", shouldDisable);
        if (shouldDisable) {
          label.setAttribute("aria-disabled", "true");
        } else {
          label.removeAttribute("aria-disabled");
        }
      });
    }
    pillsContainer.innerHTML = "";
    checkedBoxes.forEach((cb) => {
      const checkOption = cb.closest(".hulkapps_check_option");
      const name =
        checkOption?.getAttribute("aria-label") || cb.value || "Ingredient";
      const pill = document.createElement("span");
      pill.className = "mto-pill";
      const pillName = document.createElement("span");
      pillName.textContent = name;
      const removeBtn = document.createElement("button");
      removeBtn.type = "button";
      removeBtn.className = "mto-pill__remove";
      removeBtn.setAttribute("aria-label", `Remove ${name}`);
      removeBtn.textContent = "×";
      removeBtn.addEventListener("click", () => {
        // Click the parent label to trigger Hulk's own toggle handler
        if (checkOption) checkOption.click();
        updateSummary();
      });
      pill.appendChild(pillName);
      pill.appendChild(removeBtn);
      pillsContainer.appendChild(pill);
    });
  }

  // Update on checkbox change and on label click (Hulk may not fire change events)
  checkboxes.forEach((cb) => {
    cb.addEventListener("change", updateSummary);
    cb.closest(".hulkapps_check_option")?.addEventListener("click", () =>
      setTimeout(updateSummary, 0)
    );
  });
  updateSummary();
}
const MTO_LOG = "[MTO-PRICE]";
function initMtoSelectPriceUpdater() {
  console.log(MTO_LOG, "initMtoSelectPriceUpdater called");
  const priceEls = document.querySelectorAll(".mto-button-price .money");
  if (!priceEls.length) {
    console.warn(
      MTO_LOG,
      "No .mto-button-price .money elements found — aborting"
    );
    return;
  }

  // Capture the server-rendered base price from each element, in minor units (pence/cents)
  const priceTargets = Array.from(priceEls).map((el, i) => {
    const baseText = el.textContent.trim();
    const baseAmount = Math.round(
      parseFloat(baseText.replace(/[^0-9.]/g, "")) * 100
    );
    const currencyPrefix = baseText.match(/^[^\d]+/)?.[0] ?? "";
    console.log(MTO_LOG, `  Price element [${i}]:`, el);
    console.log(
      MTO_LOG,
      `    base text: "${baseText}" → ${baseAmount} minor units, prefix: "${currencyPrefix}"`
    );
    return {
      el,
      baseAmount,
      currencyPrefix,
    };
  });
  console.log(
    MTO_LOG,
    `Found ${priceTargets.length} .mto-button-price .money elements`
  );

  // Log all selects present at init time
  const selectsAtInit = document.querySelectorAll(
    "select.hulkapps_option_child"
  );
  console.log(
    MTO_LOG,
    `select.hulkapps_option_child count at init: ${selectsAtInit.length}`,
    selectsAtInit
  );
  selectsAtInit.forEach((sel, i) => {
    console.log(
      MTO_LOG,
      `  Select [${i}] name="${sel.name}" id="${sel.id}"`,
      sel
    );
    Array.from(sel.options).forEach((opt, j) => {
      console.log(
        MTO_LOG,
        `    Option [${j}] value="${opt.value}" data-price="${
          opt.dataset.price ?? "(none)"
        }" text="${opt.textContent.trim()}"`
      );
    });
  });
  function updatePrice() {
    console.log(MTO_LOG, "updatePrice triggered");
    let extraMinorUnits = 0;
    const selects = document.querySelectorAll("select.hulkapps_option_child");
    console.log(
      MTO_LOG,
      `  Found ${selects.length} select.hulkapps_option_child elements`
    );
    selects.forEach((sel, i) => {
      const selected = sel.options[sel.selectedIndex];
      const rawPrice = selected?.dataset.price ?? null;
      const price = parseFloat(rawPrice ?? "0");
      const contribution = isNaN(price) ? 0 : Math.round(price * 100);
      console.log(
        MTO_LOG,
        `  Select [${i}] selectedIndex=${sel.selectedIndex} value="${
          selected?.value
        }" data-price="${rawPrice ?? "(none)"}" → ${contribution} minor units`
      );
      if (!isNaN(price)) extraMinorUnits += contribution;
    });
    const checkboxes = document.querySelectorAll(
      "input.hulk_po_checkbox:checked"
    );
    console.log(
      MTO_LOG,
      `  Found ${checkboxes.length} checked input.hulk_po_checkbox elements`
    );
    checkboxes.forEach((cb, i) => {
      const rawPrice = cb.dataset.price ?? null;
      const price = parseFloat(rawPrice ?? "0");
      const contribution = isNaN(price) ? 0 : Math.round(price * 100);
      console.log(
        MTO_LOG,
        `  Checkbox [${i}] value="${cb.value}" data-price="${
          rawPrice ?? "(none)"
        }" → ${contribution} minor units`
      );
      if (!isNaN(price)) extraMinorUnits += contribution;
    });

    // Re-query fresh in case Hulk replaced any nodes, then update all targets
    const freshPriceEls = document.querySelectorAll(".mto-button-price .money");
    console.log(
      MTO_LOG,
      `  Fresh query found ${freshPriceEls.length} .mto-button-price .money elements`
    );
    priceTargets.forEach(({ el, baseAmount, currencyPrefix }, i) => {
      const total = baseAmount + extraMinorUnits;
      const formatted = currencyPrefix + (total / 100).toFixed(2);
      const isAttached = document.contains(el);
      console.log(
        MTO_LOG,
        `  Target [${i}]: still in DOM: ${isAttached}, total: ${total} → "${formatted}"`
      );

      // Prefer the fresh node at the same index if the captured one was replaced
      const target = isAttached ? el : freshPriceEls[i] ?? null;
      if (target) {
        target.textContent = formatted;
        console.log(
          MTO_LOG,
          `  Target [${i}]: set textContent → "${target.textContent}"`
        );
      } else {
        console.warn(MTO_LOG, `  Target [${i}]: no element to update`);
      }
    });
  }

  // Event delegation — works for selects/checkboxes added at any point after init
  document.addEventListener("change", (e) => {
    if (e.target.matches("select.hulkapps_option_child")) {
      console.log(
        MTO_LOG,
        "change event on select.hulkapps_option_child",
        e.target
      );
      updatePrice();
    } else if (e.target.matches("input.hulk_po_checkbox")) {
      console.log(MTO_LOG, "change event on input.hulk_po_checkbox", e.target);
      updatePrice();
    } else {
      console.log(
        MTO_LOG,
        "change event on non-matching element:",
        e.target.tagName,
        e.target.className
      );
    }
  });
  console.log(MTO_LOG, "change listener attached to document");
}

// Close any open cb_render accordion when the user clicks outside of it or
// presses Escape. Attached once at init — the per-block click handler in
// injectOptionSearchInputs handles the in-block toggle, this handles dismiss.
function closeOpenAccordions() {
  document
    .querySelectorAll(".hulkapps_option.cb_render.cb_render--open")
    .forEach((block) => {
      block.classList.remove("cb_render--open");
      const name = block.querySelector(".hulkapps_option_name");
      if (name) name.setAttribute("aria-expanded", "false");
    });
}
function setupAccordionDismiss() {
  if (setupAccordionDismiss._attached) return;
  setupAccordionDismiss._attached = true;
  document.addEventListener("click", (e) => {
    if (!e.target.closest(".hulkapps_option.cb_render")) {
      closeOpenAccordions();
    }
  });
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") closeOpenAccordions();
  });
}

// Use a MutationObserver to detect when Hulk renders .hulkapps_option.cb_render elements
const hulkObserver = new MutationObserver(() => {
  if (document.querySelector(".hulkapps_option.cb_render")) {
    hulkObserver.disconnect();
    injectOptionSearchInputs();
    initMtoSelectPriceUpdater();
    updateMtoButtonState();
    injectSelectLabels();
    injectIngredientHeaderLabels();
    rewriteIngredientToggleText();
    watchHulkContainerForRerender();
    setupAccordionDismiss();
  }
});
hulkObserver.observe(document.body, {
  childList: true,
  subtree: true,
});

// Pull the placeholder option (value="") text out of each Hulkapps select,
// render it as a visible label immediately before the select, and then drop
// the placeholder so the first real option becomes the default selection.
// A change event is dispatched so the MTO price updater and button-state
// validator pick up the new selected value. Idempotent: skips selects that
// already have a sibling .mto-select-label.
function injectSelectLabels() {
  document
    .querySelectorAll(
      '[id^="hulkapps_custom_options_"] .hulkapps_option_value select'
    )
    .forEach((select) => {
      const previous = select.previousElementSibling;
      if (previous && previous.classList.contains("mto-select-label")) return;
      const placeholder = Array.from(select.options).find(
        (opt) => opt.value === ""
      );
      const labelText = placeholder?.textContent.trim();
      if (!labelText) return;
      const label = document.createElement("label");
      label.className = "mto-select-label";
      label.textContent = labelText;
      if (select.id) label.htmlFor = select.id;
      select.insertAdjacentElement("beforebegin", label);
      placeholder.remove();
      select.dispatchEvent(
        new Event("change", {
          bubbles: true,
        })
      );
    });
}

// Equivalent of injectSelectLabels for the cb_render (ingredient accordion)
// blocks: read aria-label off .hulkapps_option_name and inject a visible label
// immediately before it. Idempotent on a sibling .mto-cb-render-label.
function injectIngredientHeaderLabels() {
  document
    .querySelectorAll(
      '[id^="hulkapps_custom_options_"] .hulkapps_option.cb_render .hulkapps_option_name'
    )
    .forEach((nameEl) => {
      const previous = nameEl.previousElementSibling;
      if (previous && previous.classList.contains("mto-cb-render-label")) {
        return;
      }
      const labelText = nameEl.getAttribute("aria-label")?.trim();
      if (!labelText) return;
      const label = document.createElement("label");
      label.className = "mto-cb-render-label";
      label.textContent = labelText;
      nameEl.insertAdjacentElement("beforebegin", label);
    });
}

// Replace the inner contents of the direct child <div> of each cb_render
// .hulkapps_option_name with the literal word "Ingredients" — Hulkapps
// renders something like "+ Add Ingredients" by default and the design
// calls for just "Ingredients". Idempotent.
function rewriteIngredientToggleText() {
  document
    .querySelectorAll(
      '[id^="hulkapps_custom_options_"] .hulkapps_option.cb_render .hulkapps_option_name'
    )
    .forEach((nameEl) => {
      const child = nameEl.querySelector(":scope > div");
      if (!child) return;
      if (child.textContent.trim() === "Ingredients") return;
      child.textContent = "Ingredients";
    });
}

// Hulkapps occasionally replaces .hulkapps_option_value contents (the price
// updater code already handles this for selects). When that happens our
// injected labels are removed alongside the original elements, so re-run
// injection on any DOM changes inside the Hulkapps container.
function watchHulkContainerForRerender() {
  const container = document.querySelector('[id^="hulkapps_custom_options_"]');
  if (!container) return;
  const observer = new MutationObserver(() => {
    injectSelectLabels();
    injectIngredientHeaderLabels();
    rewriteIngredientToggleText();
  });
  observer.observe(container, {
    childList: true,
    subtree: true,
  });
}

// Cache of MTO buy-buttons wrappers, populated once buttons are found below.
let mtoBuyButtonsCache = [];

// Pure validation: checks whether the current Hulkapps selections satisfy
// the requirements for this MTO product. Returns booleans for each axis so
// the click handler can build a tailored error message.
function validateMtoSelections() {
  const potencyElement =
    document.querySelector('#potency input[name="properties[Potency]"]') ||
    document.querySelector('#potency select[name="properties[Potency]"]');
  const formAndSizeElement =
    document.querySelector('input[name="properties[Form and Size]"]') ||
    document.querySelector('select[name="properties[Form and Size]"]') ||
    document.querySelector('input[name="properties[Bach™ Form and Size]"]') ||
    document.querySelector('select[name="properties[Bach™ Form and Size]"]');
  const ingredientElements = document.querySelectorAll(
    "label.hulkapps_check_option input"
  );
  const requiresPotency = !!potencyElement;
  const requiresFormAndSize = !!formAndSizeElement;
  const requiresIngredient = ingredientElements.length > 0;

  // .value works uniformly for inputs and selects: when the empty-value
  // placeholder is still present (before injectSelectLabels has run on a
  // freshly rendered select), select.value is "" and validation correctly
  // says invalid. Once the placeholder is removed, the first real option
  // is auto-selected and select.value is its (non-empty) value.
  const hasPotencyValue = requiresPotency ? !!potencyElement.value : true;
  const hasFormAndSizeValue = requiresFormAndSize
    ? !!formAndSizeElement.value
    : true;
  const hasCheckedIngredient = requiresIngredient
    ? Array.from(ingredientElements).some((element) => element.checked)
    : true;
  return {
    valid: hasPotencyValue && hasFormAndSizeValue && hasCheckedIngredient,
    hasPotencyValue,
    hasFormAndSizeValue,
    hasCheckedIngredient,
  };
}
function updateMtoButtonState() {
  if (mtoBuyButtonsCache.length === 0) return;
  const { valid } = validateMtoSelections();
  mtoBuyButtonsCache.forEach((btn) => {
    btn.classList.toggle("mto-atc--inactive", !valid);
  });
}
function createErrorBanner(errorText, elementBelowError) {
  //Adding the error banner
  const errorBanner = document.createElement("div");
  errorBanner.classList.add("banner", "banner--error", "justify-center");
  errorBanner.setAttribute("role", "alert");
  errorBanner.style.gridColumn = "1/-1";
  errorBanner.style.marginBottom = "1rem";
  errorBanner.textContent = errorText;
  errorBanner.style.gridTemplateColumns = "unset";

  //Adding the error message above the element
  elementBelowError.parentNode.insertBefore(errorBanner, elementBelowError);
}

//The below code adds an error banner above the Made to Order Add to Cart button when the price is zero, and prevents the ATC button from working.
//Initially the price is set to zero, until the app Hulkapps updates the price. The price only updates after the customer has selected a Form + size
document.addEventListener("DOMContentLoaded", () => {
  const url = window.location.href;

  // Only run if the URL contains 'made-to-order'
  if (!url.includes("made-to-order")) {
    return; // exit early if not a made-to-order PDP
  }

  // Array of MTO templates
  const mtoTemplates = ["bach-made-to-order", "homeopathy-made-to-order"];

  // Poll until the MTO buttons exist
  let checkElements = setInterval(() => {
    //Finding all MTO buttons for all templates
    let mtoBuyButtons = [];
    mtoTemplates.forEach((template) => {
      mtoBuyButtons.push(
        ...document.querySelectorAll(`buy-buttons[template="${template}"]`)
      );
    });
    if (mtoBuyButtons.length > 0) {
      //clearing the interval once all MTO buttons are found
      clearInterval(checkElements);

      // Cache buttons and apply initial visual state. validateMtoSelections()
      // returns valid=true when no required fields exist yet (Hulkapps not
      // loaded), so re-running on hulkObserver and on change events keeps the
      // disabled class in sync once Hulkapps renders its inputs.
      mtoBuyButtonsCache = mtoBuyButtons;
      updateMtoButtonState();

      // Re-validate on any form change. Delegating on document covers the
      // selects/checkboxes Hulkapps injects late without needing to re-bind.
      document.addEventListener("change", () => {
        updateMtoButtonState();
      });

      // Hulkapps toggles checkboxes via label clicks and may not fire change
      // synchronously, so revalidate on a microtask after such clicks.
      document.addEventListener("click", (e) => {
        if (e.target.closest(".hulkapps_check_option")) {
          setTimeout(updateMtoButtonState, 0);
        }
      });

      // Attaching click handlers for each MTO button
      mtoBuyButtons.forEach((mtoBuyButton) => {
        mtoBuyButton.addEventListener("click", (e) => {
          function errorBanner(message) {
            e.preventDefault();
            return createErrorBanner(message, mtoBuyButton);
          }
          const { hasPotencyValue, hasFormAndSizeValue, hasCheckedIngredient } =
            validateMtoSelections();

          // Remove any existing error banners first
          const existingBanner = mtoBuyButton.previousElementSibling;
          if (existingBanner?.classList.contains("banner--error")) {
            existingBanner.remove();
          }
          let errorMessage = "";
          if (
            !hasPotencyValue ||
            !hasFormAndSizeValue ||
            !hasCheckedIngredient
          ) {
            errorMessage = "Please select all of the required options.";
          }

          // 000
          if (
            !hasPotencyValue &&
            !hasFormAndSizeValue &&
            !hasCheckedIngredient
          ) {
            errorMessage =
              "Please select your potency, form and size and at least one ingredient from the drop down";
          }
          // 001
          if (
            !hasPotencyValue &&
            !hasFormAndSizeValue &&
            hasCheckedIngredient
          ) {
            errorMessage =
              "Please select your potency and form and size from the drop down";
          }
          // 010
          if (
            !hasPotencyValue &&
            hasFormAndSizeValue &&
            !hasCheckedIngredient
          ) {
            errorMessage =
              "Please select your potency and at least one ingredient from the drop down";
          }
          // 100
          if (
            hasPotencyValue &&
            !hasFormAndSizeValue &&
            !hasCheckedIngredient
          ) {
            errorMessage =
              "Please select your form and size and at least one ingredient from the drop down";
          }
          // 011
          if (!hasPotencyValue && hasFormAndSizeValue && hasCheckedIngredient) {
            errorMessage = "Please select your potency from the drop down";
          }
          // 101
          if (hasPotencyValue && !hasFormAndSizeValue && hasCheckedIngredient) {
            errorMessage =
              "Please select your form and size from the drop down";
          }
          // 110
          if (hasPotencyValue && hasFormAndSizeValue && !hasCheckedIngredient) {
            errorMessage =
              "Please select at least one ingredient from the drop down";
          }
          // 111
          if (!errorMessage) return;
          errorBanner(errorMessage);
        });
      });
    }
  }, 100); // Check every 100ms until buttons exist
});
